// Master-only internal onboarding guide (/internal/onboarding).
// Content comes from content/onboarding/*.json via GET /api/onboarding/guide;
// screenshots are auto-refreshed weekly server-side.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { BookOpen, Clock, AlertTriangle, ImageOff } from "lucide-react";

interface GuideStep {
  heading: string;
  body: string;
  screenshot?: string | null;
}
interface GuideSection {
  id: string;
  order: number;
  title: string;
  intro: string;
  steps: GuideStep[];
}
interface ShotMeta {
  capturedAt: string | null;
  error?: string | null;
}
interface GuideResponse {
  sections: GuideSection[];
  meta: {
    lastCheckedAt: string | null;
    nextCheckAt: string | null;
    screenshots: Record<string, ShotMeta>;
  };
  captureConfigured: boolean;
  captureRunning: boolean;
  lastCaptureError?: string | null;
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "never";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function GuideScreenshot({
  shotKey,
  meta,
}: {
  shotKey: string;
  meta: ShotMeta | undefined;
}) {
  const [failed, setFailed] = useState(false);
  if (!meta?.capturedAt || failed) {
    return (
      <div
        className="flex items-center gap-2 rounded-md border border-dashed border-border bg-muted/40 px-4 py-6 text-sm text-muted-foreground"
        data-testid={`placeholder-screenshot-${shotKey}`}
      >
        <ImageOff className="h-4 w-4" />
        Screenshot not captured yet
      </div>
    );
  }
  return (
    <figure className="space-y-1.5">
      <div className="relative overflow-hidden rounded-md border border-border">
        <img
          src={`/api/onboarding/screenshots/${shotKey}?v=${encodeURIComponent(meta.capturedAt)}`}
          alt={`Screenshot: ${shotKey}`}
          className="w-full"
          loading="lazy"
          onError={() => setFailed(true)}
          data-testid={`img-screenshot-${shotKey}`}
        />
        <div className="absolute right-2 top-2 flex flex-col items-end gap-1">
          {meta.error && (
            <Badge
              variant="destructive"
              className="gap-1"
              data-testid={`badge-capture-failed-${shotKey}`}
            >
              <AlertTriangle className="h-3 w-3" />
              Last capture failed — showing previous screenshot
            </Badge>
          )}
        </div>
      </div>
      <figcaption className="text-xs text-muted-foreground">
        Captured {fmtDate(meta.capturedAt)}
        {meta.error ? ` · last refresh attempt failed: ${meta.error}` : ""}
      </figcaption>
    </figure>
  );
}

export default function InternalOnboardingPage() {
  const { data, isLoading } = useQuery<GuideResponse>({
    queryKey: ["/api/onboarding/guide"],
    refetchInterval: (q) => (q.state.data?.captureRunning ? 5000 : false),
  });

  if (isLoading || !data) {
    return (
      <div className="mx-auto max-w-4xl space-y-4 p-6">
        <Skeleton className="h-10 w-72" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  const shots = data.meta.screenshots || {};

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6 pb-24">
      <header className="space-y-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-bold tracking-tight" data-testid="text-onboarding-title">
            Persona OS Onboarding Guide
          </h1>
          <Badge variant="secondary">Internal · Master only</Badge>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
          <span className="inline-flex items-center gap-1" data-testid="text-last-checked">
            <Clock className="h-3.5 w-3.5" />
            Last checked: {fmtDate(data.meta.lastCheckedAt)}
          </span>
          <span data-testid="text-next-check">Next check: {fmtDate(data.meta.nextCheckAt)}</span>
        </div>
      </header>

      {data.lastCaptureError && !data.captureRunning && (
        <div
          className="flex items-start gap-3 rounded-md border border-destructive/40 bg-destructive/10 p-4"
          data-testid="banner-capture-error"
        >
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
          <div className="space-y-1 text-sm">
            <p className="font-medium">Last screenshot capture failed</p>
            <p className="text-muted-foreground break-words">
              {data.lastCaptureError} — existing screenshots (if any) are still shown; the
              capture retries automatically.
            </p>
          </div>
        </div>
      )}

      <nav className="flex flex-wrap gap-2" data-testid="nav-section-links">
        {data.sections.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground hover-elevate"
            data-testid={`link-section-${s.id}`}
          >
            {s.title}
          </a>
        ))}
      </nav>

      {data.sections.map((section, idx) => (
        <section key={section.id} id={section.id} className="scroll-mt-20 space-y-4">
          <div className="space-y-1.5">
            <h2 className="text-xl font-semibold tracking-tight" data-testid={`text-section-title-${section.id}`}>
              {idx + 1}. {section.title}
            </h2>
            <p className="text-sm leading-relaxed text-muted-foreground">{section.intro}</p>
          </div>
          <div className="space-y-4">
            {section.steps.map((step, si) => (
              <Card key={si} data-testid={`card-step-${section.id}-${si}`}>
                <CardContent className="space-y-3 p-5">
                  <h3 className="font-medium">{step.heading}</h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                  {step.screenshot && (
                    <GuideScreenshot shotKey={step.screenshot} meta={shots[step.screenshot]} />
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ))}

      <footer className="border-t border-border pt-4 text-xs text-muted-foreground">
        <p>Screenshots refresh automatically every week by default.</p>
      </footer>
    </div>
  );
}
